import useMethodSystem from "../../useMethodSystem";
import Toggle from "../../../Toggle";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import DefaultOperatorSettings from "./DefaultOperatorSettings";
import PaybisSettings from "./PaybisSettings";
import { Button } from "@oc-labs/wt-storybook";
import { Plus } from "lucide-react";
import useRamp from "../../../../hooks/useRamp";

export default function OperatorSelector() {
  const { availableOperators, currencies, operators, setOperators } = useMethodSystem();
  const { paybisPairs: getPaybisPairs } = useRamp();
  const [paybisPairs, setPaybisPairs] = useState([]);
  const [newOperator, setNewOperator] = useState({
    currencyId: "",
    operatorId: "",
    paymentMethods: { card: false, bankTransfer: false },
    params: [],
  })

  const hasPaybis = operators.some(op => op.operatorId === 'paybis') || newOperator.operatorId === 'paybis';
  useEffect(() => {
    if (!hasPaybis || paybisPairs.length > 0) return;
    getPaybisPairs().then(setPaybisPairs).catch(() => {});
  }, [hasPaybis]);

  const handleAddOperator = () => {
    if(operators.find(op => op.operatorId === newOperator.operatorId && op.currencyId === newOperator.currencyId)) {
      return toast.error("Operator with this currency already added");
    }
    setOperators(prev => ([
      ...prev,
      newOperator
    ]));
    setNewOperator({
      currencyId: "",
      operatorId: "",
      paymentMethods: { card: false, bankTransfer: false },
      params: [],
    });
  }

  const deleteOperator = (operatorId, currencyId) => {
    setOperators(prevOperators => prevOperators.filter(op => !(op.operatorId === operatorId && op.currencyId === currencyId)));
  }

  const toggleMethod = (operatorId, currencyId, method) => {
    setOperators(prevOperators => prevOperators.map(op => {
      if(op.operatorId === operatorId && op.currencyId === currencyId) {
        return {
          ...op,
          paymentMethods: {
            ...op.paymentMethods,
            [method]: !op.paymentMethods[method]
          }
        }
      }
      return op;
    }));
  }

  const setOperatorParam = (operatorId, currencyId, paramKey, paramValue) => {
    setOperators(prevOperators => prevOperators.map(op => {
      if(op.operatorId === operatorId && op.currencyId === currencyId) {
        return {
          ...op,
          params: [
            ...op.params.filter(p => p.key !== paramKey),
            { key: paramKey, value: paramValue }
          ].filter(Boolean)
        }
      }
      return op;
    }));
  }

  return (<div className="space-y-4">
      {operators.map((operator) => {
        let OperatorComponent = DefaultOperatorSettings;

        if(operator?.operatorId === 'paybis') OperatorComponent = PaybisSettings;

        return (<OperatorComponent
          key={`${operator.operatorId}-${operator.currencyId}`}
          operator={operator}
          paybisPairs={paybisPairs}
          onDelete={deleteOperator}
          onToggleMethod={toggleMethod}
          onChangeParam={setOperatorParam}
        />)
      })}

      {/* Add New Operator Card */}
      <div className="bg-card rounded-lg border border-gray-300  transition-all duration-200 hover:border-primary/50">
        <div className="p-4">

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Currency</label>
                <select
                  value={newOperator.currencyId}
                  onChange={(e) => setNewOperator((prev) => ({ ...prev, currencyId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                >
                  <option value="">Select currency</option>
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.name} ({currency.slug})
                    </option>
                  ))}
                </select>
              </div>

              {/* Operator Selector */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Provider</label>
                <select
                  value={newOperator.operatorId}
                  onChange={(e) => setNewOperator((prev) => ({ ...prev, operatorId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                >
                  <option value="">Select provider</option>
                  {availableOperators.map((operator) => (
                    <option key={operator.id} value={operator.id}>
                      {operator.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
                      <svg className="w-3 h-3 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM2 10v4a2 2 0 002 2h12a2 2 0 002-2v-4H2z" />
                      </svg>
                    </div>
                    <span className="text-sm text-foreground">Card Payment</span>
                  </div>
                  <Toggle
                    checked={newOperator.paymentMethods.card}
                    onChange={() =>
                      setNewOperator((prev) => ({
                        ...prev,
                        paymentMethods: { ...prev.paymentMethods, card: !prev.paymentMethods.card },
                      }))
                    }
                    text="Toggle card payment"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-green-100 dark:bg-green-900/30 rounded flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-green-600 dark:text-green-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z"
                          clipRule="evenodd"
                        />
                        <path d="M5 8h10v1H5V8zm0 2h10v1H5v-1z" />
                      </svg>
                    </div>
                    <span className="text-sm text-foreground">Bank Transfer</span>
                  </div>
                  <Toggle
                    checked={newOperator.paymentMethods.bankTransfer}
                    onChange={() =>
                      setNewOperator((prev) => ({
                        ...prev,
                        paymentMethods: { ...prev.paymentMethods, bankTransfer: !prev.paymentMethods.bankTransfer },
                      }))
                    }
                    text="Toggle bank transfer"
                  />
                </div>
            </div>
            <div className="flex justify-end mt-8">
              <div>
                <Button
                  onClick={handleAddOperator}
                  disabled={!newOperator.currencyId || !newOperator.operatorId}
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Operator
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}